import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Building2, TrendingUp, Clock, AlertCircle, CheckCircle,
  XCircle, Award, BarChart3, RefreshCw, Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const ASCPerformance = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('success_rate');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/asc/analytics/detailed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setAnalytics(response.data);
      }
    } catch (error) {
      console.error('Error fetching ASC analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSortedASCs = () => {
    if (!analytics || !analytics.asc_analytics) return [];
    
    return [...analytics.asc_analytics].sort((a, b) => {
      if (sortBy === 'success_rate') return b.success_rate - a.success_rate;
      if (sortBy === 'turnaround') return a.avg_turnaround_days - b.avg_turnaround_days;
      if (sortBy === 'dispatches') return b.total_dispatches - a.total_dispatches;
      return 0;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: ZOBOX_BLUE }}></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-600">No ASC data available</p>
      </div>
    );
  }

  const sortedASCs = getSortedASCs();
  const overall = analytics.overall;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center" style={{ color: ZOBOX_BLUE }}>
            <Building2 className="w-8 h-8 mr-3" />
            ASC Performance Dashboard
          </h1>
          <p className="text-gray-600 mt-1">
            Detailed analytics for all service centers
          </p>
        </div>
        <button
          onClick={fetchAnalytics}
          className="flex items-center px-4 py-2 text-white rounded-lg hover:opacity-90"
          style={{ backgroundColor: ZOBOX_BLUE }}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Overall Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4" style={{ borderColor: ZOBOX_BLUE }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Total ASC Centers</p>
              <p className="text-3xl font-bold mt-2" style={{ color: ZOBOX_BLUE }}>
                {overall.total_asc_centers}
              </p>
            </div>
            <Building2 className="w-10 h-10" style={{ color: ZOBOX_BLUE }} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Total Dispatches</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">
                {overall.total_dispatches}
              </p>
            </div>
            <BarChart3 className="w-10 h-10 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Overdue</p>
              <p className="text-3xl font-bold text-red-600 mt-2">
                {overall.total_overdue}
              </p>
            </div>
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Avg Success Rate</p>
              <p className="text-3xl font-bold text-green-600 mt-2">
                {overall.avg_success_rate}%
              </p>
            </div>
            <TrendingUp className="w-10 h-10 text-green-600" />
          </div>
        </div>
      </div>

      {/* Sort Options */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Sort by:</span>
          <button
            onClick={() => setSortBy('success_rate')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              sortBy === 'success_rate' 
                ? 'text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            style={sortBy === 'success_rate' ? { backgroundColor: ZOBOX_BLUE } : {}}
          >
            Success Rate
          </button>
          <button
            onClick={() => setSortBy('turnaround')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              sortBy === 'turnaround' 
                ? 'text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            style={sortBy === 'turnaround' ? { backgroundColor: ZOBOX_BLUE } : {}}
          >
            Turnaround Time
          </button>
          <button
            onClick={() => setSortBy('dispatches')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              sortBy === 'dispatches' 
                ? 'text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            style={sortBy === 'dispatches' ? { backgroundColor: ZOBOX_BLUE } : {}}
          >
            Total Dispatches
          </button>
        </div>
      </div>

      {/* ASC Performance Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {sortedASCs.map((asc, index) => (
          <div 
            key={index} 
            className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${
              asc.success_rate >= 90 ? 'border-green-500' :
              asc.success_rate >= 70 ? 'border-yellow-500' :
              'border-red-500'
            }`}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900">{asc.name}</h3>
                <p className="text-sm text-gray-600">{asc.city} • {asc.asc_id}</p>
                <div className="flex items-center mt-2">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < Math.floor(asc.rating) 
                          ? 'text-yellow-400 fill-current' 
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                  <span className="ml-2 text-sm text-gray-600">({asc.rating})</span>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                asc.is_active 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {asc.is_active ? 'Active' : 'Inactive'}
              </div>
            </div>

            {/* Specialization */}
            <div className="mb-4">
              <p className="text-xs text-gray-600 mb-2">Specialization:</p>
              <div className="flex flex-wrap gap-2">
                {asc.specialization.map((spec, idx) => (
                  <span 
                    key={idx}
                    className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                  >
                    {spec}
                  </span>
                ))}
              </div>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <p className="text-xs text-gray-600">Total</p>
                <p className="text-xl font-bold text-gray-900">{asc.total_dispatches}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-600">Pending</p>
                <p className="text-xl font-bold text-orange-600">{asc.pending + asc.in_repair}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-600">Returned</p>
                <p className="text-xl font-bold text-green-600">{asc.returned}</p>
              </div>
            </div>

            {/* Performance Bars */}
            <div className="space-y-3">
              {/* Success Rate */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">Success Rate</span>
                  <span className={`text-sm font-bold ${
                    asc.success_rate >= 90 ? 'text-green-600' :
                    asc.success_rate >= 70 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {asc.success_rate}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      asc.success_rate >= 90 ? 'bg-green-500' :
                      asc.success_rate >= 70 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${asc.success_rate}%` }}
                  />
                </div>
              </div>

              {/* Turnaround Time */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">Avg Turnaround</span>
                  <span className="text-sm font-bold text-gray-900 flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    {asc.avg_turnaround_days} days
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      asc.avg_turnaround_days <= 5 ? 'bg-green-500' :
                      asc.avg_turnaround_days <= 10 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${Math.min((asc.avg_turnaround_days / 15) * 100, 100)}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Overdue Alert */}
            {asc.overdue_count > 0 && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center text-red-700">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  <span className="text-sm font-medium">
                    {asc.overdue_count} overdue dispatch(es)
                  </span>
                </div>
              </div>
            )}

            {/* Performance Badge */}
            <div className="mt-4 pt-4 border-t">
              {asc.success_rate >= 90 ? (
                <div className="flex items-center text-green-600">
                  <Award className="w-5 h-5 mr-2" />
                  <span className="font-semibold">Top Performer</span>
                </div>
              ) : asc.success_rate >= 70 ? (
                <div className="flex items-center text-yellow-600">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span className="font-semibold">Good Performance</span>
                </div>
              ) : (
                <div className="flex items-center text-red-600">
                  <XCircle className="w-5 h-5 mr-2" />
                  <span className="font-semibold">Needs Improvement</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {sortedASCs.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-600 text-lg">No ASC centers registered</p>
          <p className="text-gray-500 text-sm mt-2">Add ASC partners to start tracking performance</p>
        </div>
      )}
    </div>
  );
};

export default ASCPerformance;
